import { useMemo } from "react";
import { InputProps, Props } from "../../../types";
import { Button, ResponsiveSlider } from "../../general";
import { getFormattedTimeIn25Min } from "./helpers";

import "./style.css";
import "../../../styles/typography.css";
import interactLogo from "../../../images/interac.svg";
import { classNameParser } from "../../../helpers";

type CostSimulatorProps = Props<{ onApply: () => void; title: string; timeText: string }>;

export const CostSimulator = (props: CostSimulatorProps) => {
  const formattedTime = useMemo(() => getFormattedTimeIn25Min(), []);
  return (
    <div className={classNameParser("cost-simulator--container", props.className)} style={props.style}>
      <div className="cost-simulator--top-container">
        <div className="flex flex-col mx-auto mt-[55px] mb-[37px] sm:max-w-[354px]">
          <p className="cost-simulator--title">{props.title}</p>
          <p className="cost-simulator--time-text">
            {props.timeText} <span className="text-feedback-good">{formattedTime}</span>
          </p>
        </div>
        <div className="cost-simulator--simulator-container">{props.children}</div>
      </div>
      <div className="cost-simulator--bottom-container">
        <Button
          className="my-[22px] sm:w-full sm:max-w-[354px]"
          appearance="primary"
          size="large"
          onClick={props.onApply}
        >
          24 hour service · Apply now
        </Button>
      </div>
    </div>
  );
};

export const CostSimulatorLoan = (props: InputProps<number>) => (
  <>
    {props.label && (
      <div className="z-10 mb-[1.25rem]">
        <label className="subtitle-medium">{props.label}</label>
      </div>
    )}
    <div className="mx-auto">
      <ResponsiveSlider {...props} />
    </div>
  </>
);

type RadioGroupOptionsType<V extends string | number> =
  | [
      { label: string; value: V; subtext?: undefined },
      { label: string; value: V; subtext?: undefined },
      { label: string; value: V; subtext?: undefined }
    ]
  | [
      { label: string; value: V; subtext: string },
      { label: string; value: V; subtext: string },
      { label: string; value: V; subtext: string }
    ];

type CostSimulatorRadioGroupProps<V extends string | number> = InputProps<
  V,
  {
    options: RadioGroupOptionsType<V>;
    onLearnMore?: () => void;
  }
>;

export const CostSimulatorRadioGroup = <V extends string | number>(props: CostSimulatorRadioGroupProps<V>) => (
  <>
    <div className="flex justify-between w-full">
      {props.label && <label className="caption">{props.label}</label>}
      {props.onLearnMore && (
        <div className="caption-no-color global--link" onClick={props.onLearnMore}>
          Learn more
        </div>
      )}
    </div>
    <div className="grid grid-cols-3 gap-[0.5rem] justify-items-stretch mt-[1.25rem] mb-[1.75rem] w-full">
      {props.options.map((option) => (
        <div
          key={option.value}
          onClick={() => props.onChange && props.onChange(option.value)}
          className={`cost-simulator--radio${option.value === props.value ? " active" : ""}`}
        >
          {option.subtext ? (
            <>
              <p
                className={`cost-simulator--radio-small-label ${
                  option.value === props.value ? "!text-interface-50" : ""
                }`}
              >
                {option.value}
              </p>
              <p
                className={`cost-simulator--radio-subtext ${
                  option.value === props.value ? "!text-interface-50" : ""
                }`}
              >
                {`${option.subtext}`}
              </p>
            </>
          ) : (
            option.label
          )}
        </div>
      ))}
    </div>
  </>
);

export const CostSimulatorSummaryBanner = (props: Props) => (
  <div className="inline-flex box-content max-w-[314px] md:!max-w-[279px] sm:!max-w-[280px] items-center mb-[1.25rem] px-[37px]">
    <img src={interactLogo.src} alt="interac" className="mr-[11px]" />
    <p className="cost-simulator--summary-banner">{props.children}</p>
  </div>
);
